/**
 * mail_settings/{docId} の読み書き。
 * 仕様書: docs/01_spec/spec_mail_system.md B-1.6, B-2.1
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { safeSetDoc } from '@/lib/safeWrite';

import type {
  MailCompanySettings,
  MailFooterSettings,
  MailGmailToken,
  MailPreferencesSettings,
  MailStaffSettings,
} from '@goenhub/shared';

import { db } from '@/lib/firebase';

import { useAuth } from './useAuth';

function mailSettingRef(docId: string) {
  return doc(db, 'mail_settings', docId);
}

async function readMailSetting<T>(docId: string): Promise<T | null> {
  const snap = await getDoc(mailSettingRef(docId));
  return snap.exists() ? (snap.data() as T) : null;
}

// ─────────────────────────────────────────────
// 会社情報
// ─────────────────────────────────────────────
export function useMailCompany() {
  return useQuery({
    queryKey: ['mail_settings', 'company'],
    queryFn: () => readMailSetting<MailCompanySettings>('company'),
  });
}

export function useUpdateMailCompany() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: Partial<MailCompanySettings>) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        mailSettingRef('company'),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_settings', 'company'] }),
  });
}

// ─────────────────────────────────────────────
// 担当者
// ─────────────────────────────────────────────
export function useMailStaff() {
  return useQuery({
    queryKey: ['mail_settings', 'staff'],
    queryFn: () => readMailSetting<MailStaffSettings>('staff'),
  });
}

export function useUpdateMailStaff() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: Partial<MailStaffSettings>) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        mailSettingRef('staff'),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_settings', 'staff'] }),
  });
}

// ─────────────────────────────────────────────
// フッター
// ─────────────────────────────────────────────
export function useMailFooter() {
  return useQuery({
    queryKey: ['mail_settings', 'footer'],
    queryFn: () => readMailSetting<MailFooterSettings>('footer'),
  });
}

export function useUpdateMailFooter() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: Partial<MailFooterSettings>) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        mailSettingRef('footer'),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_settings', 'footer'] }),
  });
}

// ─────────────────────────────────────────────
// 環境設定（デフォルトモデル / 言語 / ゴミ箱保持）
// ─────────────────────────────────────────────
export function useMailPreferences() {
  return useQuery({
    queryKey: ['mail_settings', 'preferences'],
    queryFn: () => readMailSetting<MailPreferencesSettings>('preferences'),
  });
}

export function useUpdateMailPreferences() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: Partial<MailPreferencesSettings>) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        mailSettingRef('preferences'),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_settings', 'preferences'] }),
  });
}

// ─────────────────────────────────────────────
// Gmail token 状態（自身の uid のみ閲覧可）
// ─────────────────────────────────────────────
export interface GmailTokenStatus {
  authorized: boolean;
  authorizedEmail: string | null;
  revoked: boolean;
}

export function useGmailTokenStatus(): GmailTokenStatus | null {
  const { user } = useAuth();
  const [status, setStatus] = useState<GmailTokenStatus | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setStatus(null);
      return undefined;
    }
    const ref = doc(db, 'mail_gmail_tokens', user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setStatus({ authorized: false, authorizedEmail: null, revoked: false });
          return;
        }
        const data = snap.data() as MailGmailToken & { revokedAt?: unknown };
        const revoked = Boolean(data.revokedAt);
        setStatus({
          authorized: !revoked,
          authorizedEmail: data.authorizedEmail ?? null,
          revoked,
        });
      },
      () => setStatus({ authorized: false, authorizedEmail: null, revoked: false }),
    );
    return () => unsub();
  }, [user?.uid]);

  return status;
}
